import type { TodoIntegrationType } from '@demind-inc/core';

import { Path } from '../routing/path';
import { isDesktopApp } from './environment';

export const DESKTOP_TODO_OAUTH_WEB_CALLBACK_ORIGIN =
  process.env.REACT_APP_ENV === 'staging'
    ? 'https://staging.lifestack.ai'
    : 'https://app.lifestack.ai';
export const DESKTOP_TODO_OAUTH_LOOPBACK_CALLBACK_ORIGIN = 'http://127.0.0.1:53682';

const DESKTOP_TODO_OAUTH_LOOPBACK_TYPES = new Set<TodoIntegrationType>(['clickup', 'ticktick']);
const DESKTOP_TODO_OAUTH_LOOPBACK_PATHS = new Set<string>([
  Path.CALLBACK.CLICKUP_CALLBACK,
  Path.CALLBACK.TICKTICK_CALLBACK,
]);

export const TODO_OAUTH_CALLBACK_PATHS: Partial<Record<TodoIntegrationType, string>> = {
  todoist: Path.CALLBACK.TODOIST_CALLBACK,
  trello: Path.CALLBACK.TRELLO_CALLBACK,
  ticktick: Path.CALLBACK.TICKTICK_CALLBACK,
  clickup: Path.CALLBACK.CLICKUP_CALLBACK,
  notion: Path.CALLBACK.NOTION_CALLBACK,
  github: Path.CALLBACK.GITHUB_CALLBACK,
  'google-tasks': Path.CALLBACK.GOOGLE_TASKS_CALLBACK,
};

export const getTodoOAuthCallbackPath = (todoType: TodoIntegrationType) =>
  TODO_OAUTH_CALLBACK_PATHS[todoType] || `/callback/${todoType}`;

export const getTodoOAuthCallbackOrigin = (
  todoType?: TodoIntegrationType,
  callbackPath?: string
) => {
  if (isDesktopApp()) {
    if (
      (todoType && DESKTOP_TODO_OAUTH_LOOPBACK_TYPES.has(todoType)) ||
      (callbackPath && DESKTOP_TODO_OAUTH_LOOPBACK_PATHS.has(callbackPath))
    ) {
      return DESKTOP_TODO_OAUTH_LOOPBACK_CALLBACK_ORIGIN;
    }

    return DESKTOP_TODO_OAUTH_WEB_CALLBACK_ORIGIN;
  }

  return window.location.origin;
};

export const buildTodoOAuthCallbackUrl = (
  todoType: TodoIntegrationType,
  callbackPath: string,
  params: Record<string, string | boolean | null | undefined>
) => {
  const url = new URL(callbackPath, `${getTodoOAuthCallbackOrigin(todoType, callbackPath)}/`);

  Object.entries(params).forEach(([key, value]) => {
    if (value === null || typeof value === 'undefined') {
      return;
    }

    url.searchParams.append(key, String(value));
  });

  return url.toString();
};

const getStateCallbackUrl = (state: string | null, callbackPath: string) => {
  if (!state) {
    return null;
  }

  try {
    const stateUrl = new URL(state);
    return stateUrl.pathname === callbackPath ? stateUrl : null;
  } catch {
    return null;
  }
};

export const getTodoOAuthExchangeRedirectUrl = (
  callbackPath: string,
  searchParams: URLSearchParams
) => {
  const stateCallbackUrl = getStateCallbackUrl(searchParams.get('state'), callbackPath);

  if (stateCallbackUrl) {
    return stateCallbackUrl.toString();
  }

  const url = new URL(callbackPath, `${getTodoOAuthCallbackOrigin(undefined, callbackPath)}/`);

  Object.entries({
    redirectTo: searchParams.get('redirectTo') || Path.DASHBOARD,
    userId: searchParams.get('userId') || undefined,
    isMobileApp: searchParams.get('isMobileApp') || 'false',
  }).forEach(([key, value]) => {
    if (typeof value !== 'undefined') {
      url.searchParams.append(key, value);
    }
  });

  return url.toString();
};

export const buildLocalTodoOAuthCallbackRoute = (callbackUrl: string) => {
  const parsedCallbackUrl = new URL(callbackUrl);
  return `${parsedCallbackUrl.pathname}${parsedCallbackUrl.search}${parsedCallbackUrl.hash}`;
};
